/**
 * Unified ContentService — public API.
 *
 * This is the single import surface for feature code:
 *
 *   import { fetchContent, getContentUrl, contentCache } from 'src/content';
 *
 * Replaces the legacy local-gallery + signed-URL modules with one contract.
 * hospitalityMenuUrls / curriculumSummaryAssetService calls with one contract.
 */

import { ContentCache, getDefaultContentCache } from './cache.js';
import { resolve, resolveSync } from './resolver.js';
import { normalizeLogicalKey } from './types.js';
import { loadBackendRoutes } from './namespaceConfig.js';

export const contentCache = getDefaultContentCache();

// Best-effort: pull backend-owned route templates at boot so the frontend never
// hardcodes them. Falls back to embedded values if the backend is unreachable.
// Skipped under NODE_ENV=test so jest never makes a network call on import.
if (typeof window !== 'undefined' && process.env.NODE_ENV !== 'test') {
  loadBackendRoutes().catch(() => {});
}

function normalizeRef(ref) {
  // radio logicalKeys are full external stream URLs; keep their scheme://host
  // so the backend proxy can re-fetch the upstream.
  const preserveHost = ref.namespace === 'radio';
  return { ...ref, logicalKey: normalizeLogicalKey(ref.logicalKey, { preserveHost }) };
}

/**
 * Resolve a content ref to a concrete URL (async; may sign CloudFront URLs).
 * @param {import('./types.js').ContentRef} ref
 * @param {object} [opts] see resolver.resolve
 * @returns {Promise<import('./types.js').ContentHandle>}
 */
export async function fetchContent(ref, opts = {}) {
  return resolve(normalizeRef(ref), { cache: contentCache, ...opts });
}

/**
 * Synchronous best-effort URL. Returns a cached hit or a non-signed candidate.
 * Returns null when signing is required — callers should then `await fetchContent`.
 */
export function getContentUrl(ref, opts = {}) {
  return resolveSync(normalizeRef(ref), { cache: contentCache, ...opts });
}

/** Clear the shared cache (e.g. on logout or env change). */
export function clearContentCache() {
  contentCache.clear();
}

export { ContentCache, resolve, resolveSync };

// Namespace routing policy + the key -> ContentRef factory (single source).
export {
  getNamespaceConfig,
  parseHospitalityKey,
  signEndpointFor,
  loadBackendRoutes,
  getBackendRoutes,
  SIGNED_NAMESPACES,
  ALL_NAMESPACES,
  namespaceForKey,
  refFromKey,
} from './namespaceConfig.js';
